import Icon from "library/components/Icon";
import { TitleFilter, TitleFilterItem, TitleFilterItems } from "./TitleFilter";
import React, { useMemo } from "react";
import type { Title } from "hooks/graphql/useTitles";
import cx from "classnames";
import { TournamentFilter } from "./TournamentFilter";
import { useBettingFilters } from "./hooks/useBettingFilters";
import { useEventCounts } from "hooks/firestore/v2/betting/useEventCounts";

import * as styles from "./BettingFilters.module.scss";
import * as titleFilterStyles from "./TitleFilter.module.scss";

type BettingFiltersProps = {
  hubSlug: string;
  setPage: (page: number) => void;
  sportsTitles: string[];
  titles: Title[];
};

const BettingFilters = ({
  hubSlug,
  setPage,
  sportsTitles,
  titles,
}: BettingFiltersProps) => {
  const { eventCounts, isLoading: eventCountsLoading } = useEventCounts();
  const [filters, setFilters] = useBettingFilters({
    page: 1,
    tab: "next",
  });

  const filteredTitles = useMemo(() => {
    return titles.filter((title) => {
      const counts =
        filters.tab === "next" ? eventCounts?.matches : eventCounts?.outrights;
      const hasCounts = (counts?.[title.slug] || 0) > 0;
      return hasCounts;
    });
  }, [titles, eventCounts, filters.tab]);

  const handleTitleSelect = (sports: string[]) => {
    setPage(1);
    setFilters({
      hub: hubSlug === "esports" ? undefined : filters?.hub,
      sports,
      page: 1,
      tour: [],
      sport: undefined,
    });
  };

  return (
    <div className={styles.filters}>
      <TitleFilter
        className={styles.titleFilter}
        selectionType={"multiple"}
        onSelect={handleTitleSelect}
        selected={sportsTitles}
        loading={eventCountsLoading}
      >
        <TitleFilterItem
          onClick={() => {
            if (filters.hub === "sports") {
              setFilters({
                sports: [],
                page: 1,
                tour: [],
                sport: null,
              });
            } else {
              // Hub is sports. Shift over 1 as hub will never be set to eSports. This is to ensure the correct filters are cleared.
              setFilters({ sports: [], page: 1, tour: [], hub: null });
            }
          }}
          className={cx({
            [titleFilterStyles.active]: sportsTitles.length === 0,
          })}
        >
          <Icon
            type={sportsTitles.length === 0 ? "all" : "close"}
            className={cx(styles.filterIcon, {
              [styles.all]: sportsTitles.length === 0,
            })}
          />
        </TitleFilterItem>

        <TitleFilterItems
          titles={filteredTitles}
          counts={
            filters.tab === "next"
              ? eventCounts?.matches
              : eventCounts?.outrights
          }
        />
      </TitleFilter>

      <TournamentFilter
        sports={sportsTitles}
        setPage={setPage}
        hubSlug={hubSlug}
      />
    </div>
  );
};

BettingFilters.displayName = "BettingFilters";

export { BettingFilters };
